import React, { useState } from "react";
import NeoCard from "../../../components/Common/NeoCard";
import NeoBadge from "../../../components/Common/NeoBadge";
import NeoSearch from "../../../components/Common/NeoSearch";
import NeoDropdown from "../../../components/Common/NeoDropdown";
import NeoTable from "../../../components/Common/NeoTable";
import Breadcrumb from "../../../components/Common/Breadcrumb";
import usePlatformAudit from "./hooks/usePlatformAudit";
import { ACTION_COLORS, ACTION_FILTER_OPTIONS } from "./constants";

const PlatformAudit = () => {
    const {
        items,
        loading,
        loadingMore,
        applyFilters,
        loadMoreIfNeeded,
        hasMore,
        totalItems
    } = usePlatformAudit();

    const [search, setSearch] = useState("");
    const [actionFilter, setActionFilter] = useState("all");

    // Filtragem local sobre os itens já carregados
    const filteredLogs = items.filter(log => {
        const q = search.toLowerCase();
        const matchesSearch =
            (log.actorName || log.user || "").toLowerCase().includes(q) ||
            (log.targetTenantId || log.school || "").toLowerCase().includes(q) ||
            (log.description || log.desc || "").toLowerCase().includes(q);
        const matchesAction = actionFilter === "all" || (log.action === actionFilter);
        return matchesSearch && matchesAction;
    });

    const handleActionFilter = (value) => {
        setActionFilter(value);
        if (value !== "all") applyFilters({ action: value });
        else applyFilters({});
    };


    const columns = [
        {
            key: "id",
            label: "Código",
            width: "10%",
            render: (row) => <span className="platform-mono fw-bold">{(row.id || "").substring(0, 8)}</span>,
        },
        {
            key: "user",
            label: "Colaborador",
            width: "20%",
            render: (row) => (
                <div>
                    <div className="fw-bold text-dark">{row.actorName || row.user || "—"}</div>
                    <span className="text-xs text-muted d-block">{row.actorEmail || ""}</span>
                </div>
            ),
        },
        {
            key: "module",
            label: "Módulo",
            width: "10%",
            render: (row) => (
                <span className="badge bg-secondary-subtle text-secondary rounded px-2 py-1 font-size-11">
                    {row.module || "—"}
                </span>
            ),
        },
        {
            key: "action",
            label: "Ação",
            width: "13%",
            render: (row) => (
                <NeoBadge color={ACTION_COLORS[row.action] || "accent"}>{row.action}</NeoBadge>
            ),
        },
        {
            key: "desc",
            label: "Descrição",
            width: "30%",
            render: (row) => <span className="text-sm text-secondary">{row.description || row.desc || "—"}</span>,
        },
        {
            key: "date",
            label: "Data e Hora",
            width: "17%",
            render: (row) => {
                if (!row.createdAt) return <span className="text-muted">—</span>;
                const ts = typeof row.createdAt === "string"
                    ? new Date(row.createdAt).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })
                    : row.createdAt?.seconds
                    ? new Date(row.createdAt.seconds * 1000).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })
                    : "—";
                return <span className="text-dark fw-semibold">{ts}</span>;
            },
        },
    ];

    const currentActionLabel = ACTION_FILTER_OPTIONS.find(o => o.value === actionFilter)?.label || "Ações";

    return (
        <div className="pb-5">
            <div className="mb-4">
                <Breadcrumb title="Auditoria" />
            </div>

            <NeoCard
                title="Histórico de Auditoria & Logs"
                subtitle={`Histórico completo de ações no painel SaaS. ${totalItems > 0 ? `${totalItems} registros no total.` : ""}`}
                headerIcon="mdi-shield-check-outline"
                thinBorder={true}
                bodyClassName="p-0"
            >
                <NeoTable
                    columns={columns}
                    data={filteredLogs}
                    loading={loading}
                    defaultItemsPerPage={20}
                    itemsPerPageOptions={[20, 50]}
                    emptyMessage="Nenhum log de auditoria encontrado"
                    emptySubMessage="As ações dos administradores serão registradas automaticamente aqui."
                    onPageChange={(page, perPage) => loadMoreIfNeeded(page, perPage)}
                    searchComponent={
                        <div className="d-flex align-items-center gap-3">
                            <NeoSearch
                                size="sm"
                                placeholder="Buscar logs..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onClear={() => setSearch("")}
                                style={{ width: "240px" }}
                            />
                            <div className="d-flex align-items-center gap-2">
                                <span className="neo-table-topbar-label m-0">Ação:</span>
                                <NeoDropdown
                                    label={currentActionLabel}
                                    variant="soft-secondary"
                                    size="sm"
                                    items={ACTION_FILTER_OPTIONS.map(o => ({
                                        label: o.label,
                                        onClick: () => handleActionFilter(o.value),
                                    }))}
                                />
                            </div>
                        </div>
                    }
                />

                {/* Indicador de carregamento de mais páginas */}
                {loadingMore && (
                    <div className="text-center py-3 border-top">
                        <div className="spinner-border spinner-border-sm text-primary" role="status" />
                        <span className="text-muted ms-2 text-sm">Carregando mais registros...</span>
                    </div>
                )}

                {/* Info de mais dados disponíveis */}
                {hasMore && !loadingMore && items.length > 0 && (
                    <div className="text-center py-2 border-top">
                        <span className="text-xs text-muted">
                            {items.length} de {totalItems} registros carregados — avance as páginas para carregar mais.
                        </span>
                    </div>
                )}
            </NeoCard>
        </div>
    );
};

export default PlatformAudit;
